
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isDOMError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      isDOMError: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a DOM-related error
    const isDOMError = 
      error.message.includes('NotFoundError') || 
      error.message.includes('can not be found here') ||
      error.message.includes('Importing a module script failed');
    
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true, 
      error,
      isDOMError 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to the console
    console.error("Error caught by ErrorBoundary:", error);
    console.error("Component stack:", errorInfo.componentStack);
    
    // Update state with error info
    this.setState({ errorInfo });

    // For DOM errors, we might want to clear React's cache
    if (this.state.isDOMError) {
      console.warn("DOM-related error detected. This may be caused by React trying to manipulate elements that no longer exist.");
    }
  }

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      isDOMError: false 
    });
    
    // If onReset callback is provided, call it
    if (this.props.onReset) {
      this.props.onReset();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="p-4 text-red-500 border border-red-200 rounded-md bg-red-50">
          <h3 className="font-medium">Something went wrong</h3>
          <p className="text-sm">{this.state.error?.message || 'An unknown error occurred'}</p>
          {this.state.isDOMError && (
            <p className="text-xs mt-1 text-orange-600">
              This appears to be a DOM-related error. It may be resolved by refreshing the page.
            </p>
          )}
          <button 
            onClick={this.resetError}
            className="mt-2 px-3 py-1 text-sm bg-red-100 hover:bg-red-200 rounded"
          >
            Try again
          </button>
          {this.state.isDOMError && (
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 ml-2 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 rounded"
            >
              Refresh page
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
